import { useConfig } from "hooks/use-config";
import React, { useEffect, useState } from "react";
import { useTheme } from "hooks/use-theme";
import { useStatsData } from "store/stats-data";
import Loading from "components/loading";

const Chart = React.lazy(() => import("react-apexcharts"));

function formatDate(inputDate: string): string {
    const date = new Date(inputDate);

    // Array of month abbreviations
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const month = months[date.getMonth()];
    const day = date.getDate();

    return `${month} ${day}`;
}

interface RevenueBarChartProps {
    height?: number;
    chartType?: "bar" | "area";
    series?: any[];
    chartColors?: string[];
}

const RevenueBarChart = ({
                             height = 400,
                             chartType = "bar",
                             chartColors = ["#4669FA", "#0CE7FA", "#FA916B"],
                         }: RevenueBarChartProps) => {
    const [config] = useConfig();
    const {theme: mode} = useTheme();

    const statsData = useStatsData.getState().statsData
    const [chart_data, setChart_data] = useState([])
    const [chart_labels, setChart_labels] = useState([])


    // Loading state to delay rendering until data is fetched
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (statsData.total_revenue != null) {
            const chart_data = statsData?.revenue_chart?.map((data) => {
                return data.revenue
            }) || [];

            const chart_labels = statsData?.revenue_chart?.map((data) => {
                return formatDate(data.date)
            }) || [];

            setChart_data(chart_data)
            setChart_labels(chart_labels)
            setIsLoading(false); // Set loading to false when data is available
        }
    }, [statsData]);

    const defaultSeries = [
        // {
        //   name: "Net Profit",
        //   data: [44, 55, 57, 56, 61, 58, 63, 60, 66],
        // },
        {
            name: "Revenue",
            data: chart_data,
        },
        // {
        //   name: "Free Cash Flow",
        //   data: [35, 41, 36, 26, 45, 48, 52, 53, 41],
        // }
    ];

    const options: any = {
        chart: {
            toolbar: {
                show: false,
            },
        },
        plotOptions: {
            bar: {
                horizontal: false,
                endingShape: "rounded",
                columnWidth: "45%",
            },
        },
        legend: {
            show: true,
            position: "top",
            horizontalAlign: "right",
            fontSize: "12px",
            fontFamily: "Inter",
            offsetY: -30,
            markers: {
                width: 8,
                height: 8,
                offsetY: -1,
                offsetX: -5,
                radius: 12,
            },
            labels: {
                colors: mode === "dark" ? "#CBD5E1" : "#475569",
            },
            itemMargin: {
                horizontal: 18,
                vertical: 0,
            },
        },
        // title: {
        //     text: `Revenue Report - ₦${statsData.total_30_day_revenue.toLocaleString()}`,
        //     align: "left",
        //     offsetY: 13,
        //     offsetX: 0,
        //     floating: false,
        //     style: {
        //         fontSize: "20px",
        //         fontWeight: "500",
        //         fontFamily: "Inter",
        //         color: mode === "dark" ? "#fff" : "#0f172a",
        //     },
        // },
        dataLabels: {
            enabled: false,
        },
        stroke: {
            show: true,
            width: 2,
            colors: ["transparent"],
        },
        yaxis: {
            labels: {
                style: {
                    colors: mode === "dark" ? "#CBD5E1" : "#475569",
                    fontFamily: "Inter",
                },
            },
        },
        xaxis: {
            categories: chart_labels,
            labels: {
                style: {
                    colors: mode === "dark" ? "#CBD5E1" : "#475569",
                    fontFamily: "Inter",
                    fontSize: "8px",
                },
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
        },
        fill: {
            opacity: 1,
        },
        tooltip: {
            y: {
                formatter: function (val: number) {
                    return "₦" + val;
                },
            },
        },
        colors: chartColors,
        grid: {
            show: false,
            borderColor: mode === "dark" ? "#334155" : "#E2E8F0",
            strokeDashArray: 10,
            position: "back",
        },
        responsive: [
            {
                breakpoint: 600,
                options: {
                    legend: {
                        position: "bottom",
                        offsetY: 8,
                        horizontalAlign: "center",
                    },
                    plotOptions: {
                        bar: {
                            columnWidth: "80%",
                        },
                    },
                },
            },
        ],
    };

    // Conditionally render loading or chart based on data availability
    return isLoading ? (
        <Loading/>
    ) : chart_data.length === 0 ? (
        <div style={{
            textAlign: "center",
            padding: "20px",
            fontSize: "18px",
            color: mode === "dark" ? "#CBD5E1" : "#475569"
        }}>
            No Data Available
        </div>
    ) : (
        <Chart
            options={options}
            series={defaultSeries}
            type={chartType}
            height={height}
            width={"100%"}
        />
    );
};

export default RevenueBarChart;